/**
 * Pinch/tap zoom for product gallery images on mobile PDP and quick add modal.
 * PDP mobile: activated when media-gallery has data-mobile-pinch-zoom="true".
 * Quick add: controlled by data-quick-add-zoom ("overlay" | "inline" | "none").
 */
class ProductMobileZoom {
  constructor() {
    this.mobileMql = window.matchMedia('(max-width: 1023px)');
    this.panzoomUrl =
      'https://cdn.jsdelivr.net/npm/@panzoom/panzoom@4.5.1/dist/panzoom.min.js';
    this.panzoomInstance = null;
    this.overlay = null;
    this.inlineZoom = null;
    this.inlineSwiper = null;
    this.boundCaptureClick = this.handleCaptureClick.bind(this);
    this.boundInlineCloseClick = this.handleInlineCloseClick.bind(this);
    this.boundTriggerPointerDown = this.handleTriggerPointerDown.bind(this);
    this.boundTriggerPointerUp = this.handleTriggerPointerUp.bind(this);
    this.boundKeyUp = this.handleKeyUp.bind(this);
    this.boundSlideChange = this.handleSlideChange.bind(this);
    this.activeGalleries = new Set();
    this.activeTriggers = new Set();
    this.boundQuickAddModals = new WeakSet();

    this.mobileMql.addEventListener('change', () => this.refresh());
    document.addEventListener('DOMContentLoaded', () => this.refresh());
    document.addEventListener('mobile-zoom:refresh', () => this.refresh());

    if (document.readyState !== 'loading') {
      this.refresh();
    }
  }

  getGalleries() {
    return document.querySelectorAll(
      'media-gallery[data-mobile-pinch-zoom="true"], media-gallery[data-quick-add-zoom]'
    );
  }

  isQuickAddContext(contextRoot) {
    return (
      !!contextRoot.closest('quick-add-modal') ||
      !!contextRoot.closest('quick-add-modal-mobile')
    );
  }

  getQuickAddZoomStyle(contextRoot) {
    return contextRoot?.dataset.quickAddZoom || 'inline';
  }

  shouldHandleContext(contextRoot) {
    if (!contextRoot) return false;

    if (this.isQuickAddContext(contextRoot)) {
      const style = this.getQuickAddZoomStyle(contextRoot);
      return style === 'overlay' || style === 'inline';
    }

    if (!contextRoot.dataset.mobilePinchZoom) return false;
    return this.mobileMql.matches;
  }

  getZoomStyle(gallery) {
    if (this.isQuickAddContext(gallery)) {
      return this.getQuickAddZoomStyle(gallery);
    }
    return 'overlay';
  }

  refresh() {
    this.closeAllZoom();
    this.teardown();
    this.bindQuickAddModals();

    this.getGalleries().forEach(gallery => {
      if (!this.shouldHandleContext(gallery)) return;
      gallery.addEventListener('click', this.boundCaptureClick, true);
      this.activeGalleries.add(gallery);
      this.bindZoomTriggers(gallery);
      this.configureGallerySwiper(gallery);

      const slideshow = gallery.querySelector('swiper-slideshow');
      const swiper = slideshow?.getSwiper?.();
      if (swiper && !swiper.__mobileZoomSlideListener) {
        swiper.on('slideChange', this.boundSlideChange);
        swiper.__mobileZoomSlideListener = true;
      }
    });
  }

  configureGallerySwiper(gallery) {
    if (!this.isQuickAddContext(gallery)) return;

    const slideshow = gallery.querySelector('swiper-slideshow');
    const swiper = slideshow?.getSwiper?.();
    if (!swiper || swiper.__mobileZoomSwiperConfigured) return;

    swiper.params.grabCursor = false;
    swiper.params.preventClicks = false;
    swiper.params.preventClicksPropagation = false;

    if (this.mobileMql.matches) {
      swiper.params.simulateTouch = true;
    } else {
      swiper.params.simulateTouch = false;
    }

    swiper.update();
    swiper.__mobileZoomSwiperConfigured = true;
  }

  bindZoomTriggers(gallery) {
    gallery.querySelectorAll('[data-zoom-image]').forEach(trigger => {
      if (this.activeTriggers.has(trigger)) return;

      trigger.addEventListener(
        'pointerdown',
        this.boundTriggerPointerDown,
        true
      );
      trigger.addEventListener('pointerup', this.boundTriggerPointerUp, true);
      this.activeTriggers.add(trigger);
    });
  }

  handleTriggerPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('[data-mobile-zoom-inline-close]')) return;

    const trigger = event.currentTarget;
    if (this.inlineZoom?.trigger === trigger) return;

    trigger.__zoomPointer = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }

  handleTriggerPointerUp(event) {
    const trigger = event.currentTarget;
    const start = trigger.__zoomPointer;
    trigger.__zoomPointer = null;

    if (!start || start.pointerId !== event.pointerId) return;
    if (event.target.closest('[data-mobile-zoom-inline-close]')) return;
    if (this.inlineZoom?.trigger === trigger) return;

    const moved =
      Math.abs(event.clientX - start.x) > 8 ||
      Math.abs(event.clientY - start.y) > 8;
    if (moved) return;

    const gallery = trigger.closest('media-gallery');
    if (!gallery || !this.shouldHandleContext(gallery)) return;

    event.preventDefault();
    event.stopPropagation();
    this.activateZoom(trigger, gallery, event);
  }

  bindQuickAddModals(root = document) {
    root
      .querySelectorAll('quick-add-modal, quick-add-modal-mobile')
      .forEach(modal => this.bindQuickAddModal(modal));
  }

  bindQuickAddModal(modal) {
    if (!modal || this.boundQuickAddModals.has(modal)) return;
    this.boundQuickAddModals.add(modal);
  }

  getZoomSrc(trigger, img) {
    const maxWidth = parseInt(trigger?.dataset?.zoomMaxWidth, 10);
    const targetWidth = maxWidth > 0 ? maxWidth : 5760;

    const candidates = [
      trigger?.dataset?.zoomSrc,
      this.getLargestSrcsetUrl(img),
      img?.currentSrc,
      img?.src,
      img?.getAttribute('src'),
    ].filter(Boolean);

    const base = candidates[0] || '';
    return this.upgradeImageUrl(base, targetWidth);
  }

  getLargestSrcsetUrl(img) {
    const srcset = img?.srcset || img?.getAttribute('srcset');
    if (!srcset) return '';

    let largestUrl = '';
    let largestWidth = 0;

    srcset.split(',').forEach(entry => {
      const parts = entry.trim().split(/\s+/);
      if (!parts.length) return;

      const url = parts[0];
      const width = parseInt(parts[1], 10) || 0;
      if (width >= largestWidth) {
        largestWidth = width;
        largestUrl = url;
      }
    });

    return largestUrl;
  }

  upgradeImageUrl(url, targetWidth = 5760) {
    if (!url) return '';

    let normalized = url.startsWith('//') ? `https:${url}` : url;
    normalized = normalized.replace(
      /_(\d+)x(\d+)?(_crop_[a-z]+)?(?=\.[^./?]+)/gi,
      ''
    );

    try {
      const parsed = new URL(normalized, window.location.origin);
      parsed.searchParams.set('width', String(targetWidth));
      parsed.searchParams.delete('height');
      return parsed.toString();
    } catch (_) {
      return normalized;
    }
  }

  waitForImageReady(img, callback) {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      callback();
    };

    img.addEventListener('load', finish, { once: true });
    img.addEventListener('error', finish, { once: true });

    if (img.complete && img.naturalWidth > 0) {
      window.requestAnimationFrame(finish);
    }
  }

  preloadImage(src) {
    return new Promise((resolve, reject) => {
      const loader = new Image();
      loader.onload = () => resolve(loader);
      loader.onerror = reject;
      loader.src = src;
    });
  }

  handleInlineCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    this.closeInlineZoom();
  }

  getZoomTriggerFromEvent(event) {
    const directTrigger = event.target.closest('[data-zoom-image]');
    if (
      directTrigger &&
      !directTrigger.closest('.product-media-gallery__thumbnail')
    ) {
      return directTrigger;
    }

    const slide = event.target.closest(
      '.swiper-slide, .product-media-gallery__grid-item'
    );
    if (!slide) return null;

    const slideTrigger = slide.querySelector('[data-zoom-image]');
    if (
      !slideTrigger ||
      slideTrigger.closest('.product-media-gallery__thumbnail')
    ) {
      return null;
    }

    return slideTrigger;
  }

  isInlineZoomInteractionTarget(target) {
    return !!target?.closest?.(
      '.product-mobile-zoom__inline-active, .product-mobile-zoom__inline-close, [data-mobile-zoom-inline-close]'
    );
  }

  activateZoom(trigger, gallery, event) {
    if (!trigger || !gallery || !this.shouldHandleContext(gallery))
      return false;
    if (trigger.closest('.product-media-gallery__thumbnail')) return false;
    if (event?.target?.closest?.('[data-mobile-zoom-inline-close]'))
      return false;

    const img = trigger.querySelector('img');
    if (!img || trigger.closest('deferred-media, product-model')) return false;

    if (this.getZoomStyle(gallery) === 'inline') {
      if (this.inlineZoom?.trigger === trigger) {
        return true;
      }

      event?.preventDefault?.();
      event?.stopPropagation?.();
      event?.stopImmediatePropagation?.();

      this.closeAllZoom();
      this.prepareInlineZoom(img, trigger, gallery, this.getFocalPointFromEvent(event));
      return true;
    }

    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.stopImmediatePropagation?.();

    this.openZoom(img, trigger);
    return true;
  }

  getFocalPointFromEvent(event) {
    if (!event || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
      return null;
    }

    return { clientX: event.clientX, clientY: event.clientY };
  }

  bindInlineCloseButton(closeBtn) {
    if (!closeBtn || closeBtn.__mobileZoomCloseBound) return;

    closeBtn.addEventListener('click', this.boundInlineCloseClick, true);
    closeBtn.__mobileZoomCloseBound = true;
  }

  teardown() {
    this.activeGalleries.forEach(gallery => {
      gallery.removeEventListener('click', this.boundCaptureClick, true);
    });
    this.activeGalleries.clear();

    this.activeTriggers.forEach(trigger => {
      trigger.removeEventListener(
        'pointerdown',
        this.boundTriggerPointerDown,
        true
      );
      trigger.removeEventListener(
        'pointerup',
        this.boundTriggerPointerUp,
        true
      );
      delete trigger.__zoomPointer;
    });
    this.activeTriggers.clear();
  }

  handleSlideChange() {
    if (this.inlineZoom) {
      this.closeInlineZoom();
    }
  }

  handleCaptureClick(event) {
    if (event.target.closest('[data-mobile-zoom-inline-close]')) return;

    const trigger = this.getZoomTriggerFromEvent(event);
    if (!trigger) return;

    const gallery = trigger.closest('media-gallery');
    if (!gallery || !this.shouldHandleContext(gallery)) return;

    if (this.isQuickAddContext(gallery)) return;

    if (this.inlineZoom && this.isInlineZoomInteractionTarget(event.target)) {
      return;
    }

    this.activateZoom(trigger, gallery, event);
  }

  handleKeyUp(event) {
    if (event.code?.toUpperCase() === 'ESCAPE') {
      this.closeAllZoom();
    }
  }

  prepareInlineZoom(img, trigger, gallery, focalPoint = null) {
    const zoomSrc = this.getZoomSrc(trigger, img);

    if (!zoomSrc) return;

    let stage = trigger.querySelector('.product-mobile-zoom__inline-stage');
    if (!stage) {
      stage = document.createElement('div');
      stage.className = 'product-mobile-zoom__inline-stage';
      trigger.appendChild(stage);
    }

    trigger.classList.add('product-mobile-zoom__inline-active');
    gallery.classList.add('product-mobile-zoom__gallery-inline-active');
    img.classList.add('product-mobile-zoom__thumbnail-hidden');

    let closeBtn = trigger.querySelector('[data-mobile-zoom-inline-close]');
    if (!closeBtn) {
      closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'product-mobile-zoom__inline-close';
      closeBtn.dataset.mobileZoomInlineClose = '';
      closeBtn.setAttribute('aria-label', 'Close zoom');
      closeBtn.innerHTML = `
        <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      `;
      trigger.appendChild(closeBtn);
    }
    this.bindInlineCloseButton(closeBtn);

    const slideshow = gallery.querySelector('swiper-slideshow');
    const swiper = slideshow?.getSwiper?.();
    if (swiper) {
      this.inlineSwiper = swiper;
      swiper.allowTouchMove = false;
    }

    document.addEventListener('keyup', this.boundKeyUp);
    this.inlineZoom = { trigger, img, gallery, stage, zoomSrc, focalPoint };

    this.preloadImage(zoomSrc)
      .then(loader => {
        if (!this.inlineZoom || this.inlineZoom.trigger !== trigger)
          return null;

        let zoomImg = stage.querySelector('.product-mobile-zoom__zoom-image');
        if (!zoomImg) {
          zoomImg = document.createElement('img');
          zoomImg.className = 'product-mobile-zoom__zoom-image';
          zoomImg.alt = img.alt || '';
          stage.appendChild(zoomImg);
        }

        zoomImg.src = zoomSrc;
        trigger.dataset.zoomSrc = zoomSrc;
        this.inlineZoom.zoomImg = zoomImg;
        this.inlineZoom.loadedWidth = loader?.naturalWidth || 0;

        return zoomImg;
      })
      .catch(() => {
        if (!this.inlineZoom || this.inlineZoom.trigger !== trigger)
          return null;

        let zoomImg = stage.querySelector('.product-mobile-zoom__zoom-image');
        if (!zoomImg) {
          zoomImg = document.createElement('img');
          zoomImg.className = 'product-mobile-zoom__zoom-image';
          zoomImg.alt = img.alt || '';
          stage.appendChild(zoomImg);
        }

        zoomImg.src = zoomSrc;
        trigger.dataset.zoomSrc = zoomSrc;
        this.inlineZoom.zoomImg = zoomImg;

        return zoomImg;
      })
      .then(zoomImg => {
        if (!zoomImg || !this.inlineZoom || this.inlineZoom.trigger !== trigger)
          return;
        this.loadPanzoom(() =>
          this.initInlinePanzoom(zoomImg, trigger, gallery)
        );
      });
  }

  initInlinePanzoom(zoomImg, trigger, gallery) {
    if (!this.inlineZoom || this.inlineZoom.trigger !== trigger) return;

    const { stage } = this.inlineZoom;

    const initPanzoom = () => {
      if (!this.inlineZoom || this.inlineZoom.trigger !== trigger) return;

      zoomImg.style.position = 'relative';
      zoomImg.style.width = 'auto';
      zoomImg.style.height = 'auto';
      zoomImg.style.maxWidth = 'none';
      zoomImg.style.maxHeight = 'none';
      zoomImg.style.objectFit = 'contain';

      if (!window.Panzoom) {
        zoomImg.style.transform = 'scale(2.5)';
        zoomImg.style.transformOrigin = 'center center';
        return;
      }

      if (this.panzoomInstance) {
        this.panzoomInstance.destroy();
        this.panzoomInstance = null;
      }

      this.panzoomInstance = Panzoom(zoomImg, {
        maxScale: 2,
        minScale: 1,
        startScale: 1,
        step: 0.25,
        contain: 'outside',
        panOnlyWhenZoomed: true,
        pinchAndPan: true,
        touchAction: 'none',
        cursor: 'grab',
        animate: true,
      });

      const initialScale = 2;
      const focalPoint = this.inlineZoom.focalPoint;

      if (
        focalPoint &&
        typeof this.panzoomInstance.zoomToPoint === 'function'
      ) {
        this.panzoomInstance.zoomToPoint(initialScale, focalPoint, {
          animate: true,
        });
      } else {
        this.panzoomInstance.zoom(initialScale, { animate: true });
      }

      const wheelHandler = event => {
        if (!this.panzoomInstance) return;
        this.panzoomInstance.zoomWithWheel(event);
      };

      const doubleClickHandler = event => {
        event.preventDefault();
        event.stopPropagation();
        this.closeInlineZoom();
      };

      stage.addEventListener('wheel', wheelHandler, { passive: false });
      zoomImg.addEventListener('dblclick', doubleClickHandler);

      this.inlineZoom.wheelHandler = wheelHandler;
      this.inlineZoom.doubleClickHandler = doubleClickHandler;
    };

    this.waitForImageReady(zoomImg, () => requestAnimationFrame(initPanzoom));
  }

  closeInlineZoom() {
    if (!this.inlineZoom) return;

    const {
      trigger,
      img,
      gallery,
      stage,
      wheelHandler,
      doubleClickHandler,
      zoomImg,
    } = this.inlineZoom;

    document.removeEventListener('keyup', this.boundKeyUp);

    if (wheelHandler && stage) {
      stage.removeEventListener('wheel', wheelHandler);
    }

    if (doubleClickHandler && zoomImg) {
      zoomImg.removeEventListener('dblclick', doubleClickHandler);
    }

    if (this.panzoomInstance) {
      this.panzoomInstance.destroy();
      this.panzoomInstance = null;
    }

    stage?.querySelector('.product-mobile-zoom__zoom-image')?.remove();
    img.classList.remove('product-mobile-zoom__thumbnail-hidden');

    trigger.querySelector('[data-mobile-zoom-inline-close]')?.remove();

    trigger.classList.remove('product-mobile-zoom__inline-active');
    gallery.classList.remove('product-mobile-zoom__gallery-inline-active');

    if (this.inlineSwiper) {
      this.inlineSwiper.allowTouchMove = true;
      this.inlineSwiper = null;
    }

    this.inlineZoom = null;
  }

  openZoom(img, trigger) {
    const zoomSrc = this.getZoomSrc(trigger, img);

    if (!zoomSrc) return;

    this.loadPanzoom(() => {
      this.showOverlay(zoomSrc, img.alt || '');
    });
  }

  loadPanzoom(callback) {
    if (window.Panzoom) {
      callback();
      return;
    }

    const existing = document.querySelector('script[data-panzoom-loader]');
    if (existing) {
      existing.addEventListener('load', callback, { once: true });
      existing.addEventListener('error', callback, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = this.panzoomUrl;
    script.dataset.panzoomLoader = '';
    script.onload = callback;
    script.onerror = callback;
    document.body.appendChild(script);
  }

  showOverlay(src, alt) {
    this.closeAllZoom();
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keyup', this.boundKeyUp);

    const overlay = document.createElement('div');
    overlay.className = 'product-mobile-zoom__overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Product image zoom');

    overlay.innerHTML = `
      <button type="button" class="product-mobile-zoom__close" data-mobile-zoom-close aria-label="Close">
        <span class="icon icon-x-small">
          <svg class="icon icon-close" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation">
            <line x1="0.353553" y1="0.353478" x2="21.3536" y2="21.3535" stroke="currentColor"/>
            <line x1="0.646447" y1="21.3535" x2="21.6464" y2="0.353478" stroke="currentColor"/>
          </svg>
        </span>
      </button>
      <div class="product-mobile-zoom__stage">
        <img src="${src}" alt="${alt.replace(/"/g, '&quot;')}" data-mobile-zoom-image />
      </div>
    `;

    overlay
      .querySelector('[data-mobile-zoom-close]')
      .addEventListener('click', () => this.closeAllZoom());

    overlay.addEventListener('click', event => {
      if (
        event.target === overlay ||
        event.target.classList.contains('product-mobile-zoom__stage')
      ) {
        this.closeAllZoom();
      }
    });

    document.body.appendChild(overlay);
    this.overlay = overlay;

    const zoomImg = overlay.querySelector('[data-mobile-zoom-image]');
    const initPanzoom = () => {
      this.panzoomInstance = Panzoom(zoomImg, {
        maxScale: 2,
        minScale: 1,
        startScale: 1,
        step: 0.25,
        contain: 'outside',
        panOnlyWhenZoomed: true,
        pinchAndPan: true,
        touchAction: 'none',
        cursor: 'grab',
        animate: true,
      });

      this.panzoomInstance.zoom(1.5, { animate: true });

      const stage = overlay.querySelector('.product-mobile-zoom__stage');
      stage?.addEventListener(
        'wheel',
        event => {
          this.panzoomInstance?.zoomWithWheel(event);
        },
        { passive: false }
      );

      let isDragging = false;
      zoomImg.addEventListener('panzoomstart', () => {
        isDragging = false;
      });
      zoomImg.addEventListener('panzoomchange', () => {
        isDragging = true;
      });
      zoomImg.addEventListener('panzoomend', () => {
        if (!isDragging) {
          this.closeAllZoom();
        }
      });
    };

    if (zoomImg.complete) {
      initPanzoom();
    } else {
      zoomImg.addEventListener('load', initPanzoom, { once: true });
    }
  }

  closeOverlay() {
    document.removeEventListener('keyup', this.boundKeyUp);

    if (this.panzoomInstance && this.overlay) {
      this.panzoomInstance.destroy();
      this.panzoomInstance = null;
    }

    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }

    document.body.classList.remove('overflow-hidden');
  }

  closeAllZoom() {
    this.closeInlineZoom();
    this.closeOverlay();
  }
}

window.ProductMobileZoom = new ProductMobileZoom();
